import { flow } from '@1x2network/flow'
import { Spine, ValueText, ValueTextSpecification } from '@1x2network/pixi_assets'
import { BasicSymbol, BasicSymbolSpec } from '@1x2network/reel_set'
import { formatPixiText } from './utils/formatCurrency'
import { Container } from 'pixi.js'
import { displayedValues, valueIds } from './constants/valueIds'

/**
 * A basic symbol with an extra collect animation
 * @author Adam Drake - <adam.drake@1x2network.com>
 * @version 1.0.0
 */
export class CustomSymbol extends BasicSymbol {
    private text?: ValueText
    public stakeSub?: () => void
    public readonly animations: {
        static: string
        activate: string
        land: string
        win: string
        collect: string
        idle: string
    }

    constructor(options: { spec?: CustomSymbolSpecification; id: number }) {
        super({ spec: options.spec, id: options.id })
        this.sortableChildren = true
        this.animations = options.spec?.animations ?? {
            static: 'static',
            activate: 'win',
            land: 'landing',
            win: 'win',
            collect: 'landing',
            idle: 'idle',
        }
        if (options.spec?.value) {
            this.text = new ValueText({ spec: options.spec.value, parent: this, format: formatPixiText })
        }
    }

    public async playIdle(): Promise<void> {
        if (this.spine) {
            this.spine.play(this.animations.idle, 0, true)
        }
    }

    public async playLanding(): Promise<void> {
        if (this.spine) {
            await flow.wait(this.spine.play(this.animations.land))
            this.blurred = false
        }
    }

    public async playCollect(): Promise<void> {
        if (this.spine) {
            await flow.wait(this.spine.play(this.animations.collect))
            this.blurred = false
        }
    }

    public async playActivate(): Promise<void> {
        if (this.spine) {
            await flow.wait(this.spine.play(this.animations.activate))
            this.blurred = false
        }
    }

    /** play win animation on the symbol */
    async playWin() {
        if (this.spine) {
            await flow.wait(this.spine.play(this.animations.win))
            this.blurred = false
        }
    }

    public updateTextValue(cash: number) {
        if (this.text && displayedValues.includes(this.id)) {
            this.text.value = cash
            this.text.visible = Boolean(cash)
        }
    }

    public addBackgroundEffect(container: Container) {
        this.addChild(container)
    }

    public get value(): number {
        return this.text?.value ?? 0
    }

    public reset() {
        super.reset()
        if (this.stakeSub) {
            this.stakeSub()
        }
        this.tint = 0xffffff
        this.alpha = 1
        this.visible = true
        this.scale.set(1)
        this.position.set(0)
        this.zIndex = 0
        this.name = null
    }

    public set tint(value: number) {
        const spine = this.spine
        if (!spine) return

        spine.tint = value
        if (!spine.autoUpdate) {
            spine.update(0)
        }
    }

    public get tint(): number {
        const spine = this.spine
        if (!spine) return 0xffffff
        return spine.tint
    }

    public set blurred(value: boolean) {
        // still use static and blur animations for regular symbols
        if (!valueIds.includes(this.id)) {
            super.blurred = value
        } else {
            // play idle animation on cash symbols that arn't blurred
            if (!value) {
                // dont restart the animation if its already playing
                if (this.spine?.state.tracks[0]?.animation.name !== this.animations.idle) {
                    this.playIdle()
                }
            }
        }
    }
}

export interface CustomSymbolSpecification extends BasicSymbolSpec {
    value?: ValueTextSpecification
    animations: {
        static: string
        activate: string
        land: string
        win: string
        collect: string
        idle: string
    }
}
